import { useMemo, type FC } from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import WizardStepper from "src/components/reusable/WizardStepper/WizardStepper";
import type { CampaignData } from "../../../EngagementInterfaces";
import CampaignPageLayout from "../../shared/CampaignPageLayout";
import CampaignFlowActions from "../../shared/CampaignFlowActions";
import ScheduleCampaignModal from "../EmailCampaign/AiEmail/components/ScheduleCampaignModal";
import EmailStatusPopup from "../EmailCampaign/AiEmail/EmailStatusPopup";
import ChooseWhatsAppTemplateStep from "./steps/ChooseWhatsAppTemplateStep";
import WhatsAppEditorStep from "./steps/WhatsAppEditorStep";
import { useWhatsAppCampaignWizard } from "./hooks/UseWhatsAppCampaignWizard";
import { useWhatsAppTemplates } from "./hooks/UseWhatsAppTemplates";
import {
  NO_LIST_NAMES,
  WHATSAPP_CAMPAIGN_STEPS,
  WHATSAPP_EDITOR_KEY,
} from "./WhatsAppCampaignConstants";
import "../EmailCampaign/AiEmail/AiCampaignWizard.scss";
import "./WhatsAppCampaignEditor.scss";

const WIZARD_KEY = "campaignFlow.aiWizard";

interface WhatsAppCampaignWizardProps {
  campaignData: Partial<CampaignData>;
}

const WhatsAppCampaignWizard: FC<WhatsAppCampaignWizardProps> = ({ campaignData }) => {
  const { t } = useTranslation();
  const templatesState = useWhatsAppTemplates();

  const {
    stepIndex,
    stepKey,
    template,
    drafts,
    media,
    audience,
    preview,
    isComplete,
    isSending,
    isScheduleOpen,
    deliveryStatus,
    selectTemplate,
    updateDraft,
    attachMedia,
    removeMedia,
    goNext,
    goBack,
    changeTemplate,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
  } = useWhatsAppCampaignWizard({
    listNames: campaignData.sendTo ?? NO_LIST_NAMES,
    campaignName: campaignData.campaignName ?? "",
    onTemplatesStale: templatesState.reload,
  });

  const isEditorStep = stepKey === "editor";

  const stepperSteps = useMemo(
    () => WHATSAPP_CAMPAIGN_STEPS.map(step => ({ key: step.key, title: t(step.titleKey) })),
    [t]
  );

  const isNextDisabled = !template;

  return (
    <CampaignPageLayout
      stepper={<WizardStepper steps={stepperSteps} currentIndex={stepIndex} />}
    >
      {isEditorStep && template ? (
        <WhatsAppEditorStep
          template={template}
          drafts={drafts}
          audience={audience}
          media={media}
          preview={preview}
          onDraftChange={updateDraft}
          onAttachMedia={attachMedia}
          onRemoveMedia={removeMedia}
          onChangeTemplate={changeTemplate}
        />
      ) : (
        <ChooseWhatsAppTemplateStep templatesState={templatesState} onUse={selectTemplate} />
      )}

      <CampaignFlowActions onBack={goBack} wide>
        {isEditorStep ? (
          <>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={openSchedule}
              disabled={!isComplete}
            >
              {t(`${WIZARD_KEY}.createCampaign.scheduleCampaign`)}
            </button>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={sendNow}
              disabled={!isComplete}
            >
              {t(`${WIZARD_KEY}.createCampaign.sendCampaign`)}
            </button>
          </>
        ) : (
          <Tooltip
            title={isNextDisabled ? t(`${WHATSAPP_EDITOR_KEY}.chooseTemplate.nextDisabledHint`) : ""}
            placement="topRight"
            arrow={false}
            overlayClassName="wa-next-tooltip"
          >
            <span className="wa-next-tooltip__trigger">
              <button
                type="button"
                className="button button--primary campaign-flow__action"
                onClick={goNext}
                disabled={isNextDisabled}
              >
                {t(`${WIZARD_KEY}.next`)}
              </button>
            </span>
          </Tooltip>
        )}
      </CampaignFlowActions>

      {isScheduleOpen && (
        <ScheduleCampaignModal
          isSubmitting={isSending}
          onCancel={closeSchedule}
          onSchedule={schedule}
        />
      )}

      {deliveryStatus && (
        <EmailStatusPopup
          type={deliveryStatus.type}
          date={deliveryStatus.date}
          time={deliveryStatus.time}
          onClose={clearDeliveryStatus}
        />
      )}
    </CampaignPageLayout>
  );
};

export default WhatsAppCampaignWizard;
